<template>
    <IconButton v-if="props.touchMode"
        :icon="icon"
        class="tertiary"
        :class="{
            invert: invert,
            'size-xl': props.size === 'size-xl',
            'size-l': props.size === 'size-l',
            'is-loading': !isLoaded,
            'is-appearing': isLoaded,
            'animate-bump': isAnimate
        }"
        :dunk="props.dunk"
        @touchend.prevent.stop="handleClick"
    />

    <IconButton v-else
        :icon="icon"
        class="tertiary"
        :class="{
            invert: invert,
            'size-xl': props.size === 'size-xl',
            'size-l': props.size === 'size-l',
            'is-loading': !isLoaded,
            'is-appearing': isLoaded,
            'animate-bump': isAnimate
        }"
        :dunk="props.dunk"
        @click.stop="handleClick"
    />
</template>

<script setup>
    import { computed, reactive, watch, onMounted, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import stores from '@/stores';
    import api from '🔗/api';
    import { TGS_DEFAULT_LIKE } from '🔗/constants';

    import { message } from '🔗/utils/utils';

    const { t } = useI18n();
    const messengers = stores.messengers();
    const others = stores.others();
    const tgs = stores.tgs();
    const favorite = api.favorite();

    const props = defineProps({
        entity: { type: String, required: true },
        id: { type: [String, null, undefined], default: null },
        type: { type: String, default: 'LIKE' },
        tone: { type: [String, null], default: null },
        themeSensetive: { type: Boolean, default: true },
        dunk: { type: Boolean, default: true },
        touchMode: { type: Boolean, default: false },
        size: { type: String, default: 'size-xl' }
    });

    const isLoaded = ref(false);
    const isAnimate = ref(false);

    const data = reactive({
    });

    const invert = computed(() => {
        if (!props.themeSensetive) {
            return false;
        }

        if (!props.tone) {
            return false;
        }

        return !((others.theme !== 'dark' && props.tone !== 'dark') || (others.theme === 'dark' && props.tone === 'dark'));
    });

    const reaction = computed(() => {
        return favorite.reactions[props.entity]?.[props.id];
    });

    const icon = computed(() => {
        if (props.type === 'LIKE') {
            return props.type === reaction.value ? 'like' : 'unlike';
        }

        if (props.type === 'DISLIKE') {
            return props.type === reaction.value ? 'dislike' : 'undislike';
        }
    });


    const getLikeStickers = async () => {
        return TGS_DEFAULT_LIKE.map(item => `${import.meta.env.VITE_APP_TGS_URL}/${item.collection}/${item.code}.tgs`);
    }

    const handleClick = (e) => {
        isAnimate.value = false;
        // Небольшой хак для рестарта анимации
        setTimeout(() => isAnimate.value = true, 10); 
        doIt(e);
    }


    const doIt = async (e) => {
        const touch = e.changedTouches ? e.changedTouches[0] : e;

        if (reaction.value && reaction.value === props.type) {
            await favorite[props.entity]?.remove(props.id);
            return;
        }

        if (props.type == 'LIKE') {
            await favorite[props.entity]?.like(props.id);
            tgs.explode(touch.pageX, touch.pageY, {
                stickers: await getLikeStickers(),
                type: 'burst',
                count: 8,
                speed: 5
            });


            // А что бы нет? Попробую
            // const items = ['queen-dust', 'knock-knock', 'seven-nation', 'eye-tiger'];
            // const randomItem = items[Math.floor(Math.random() * items.length)];
            others.vibroSong('queen-dust');

            return;
        }

        if (props.type == 'DISLIKE') {
            await favorite[props.entity]?.dislike(props.id);
            return;
        }
    }

    const checkState = async () => {
        if (!props.id || !props.entity) {
            return;
        }

        if (!api.favorite()?.[props.entity]) {
            return;
        }

        await api.favorite()?.[props.entity]?.check(props.id);

        isLoaded.value = true;
    }

    watch(
        () => props.id,
        () => {
            if (props.id) {
                isLoaded.value = false;
                checkState();
            }
        }
    );

    onMounted(() => {
        if (props.id) {
            checkState();
        }
    });
</script>


<style scoped lang="scss">
    .tertiary {
        transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), 
                    opacity 0.3s ease;
    }
    /* 1. Пока не загружено — просто невидимо, но место занято */
    .is-loading {
        opacity: 0;
        transform: scale(0);
        pointer-events: none; /* Чтобы нельзя было нажать до загрузки */
    }

    /* 2. Эффект появления при смене класса */
    .is-appearing {
        opacity: 1;
        transform: scale(1);
    }

    /* Анимация пульсации при клике */
    .animate-bump {
        animation: bump 0.4s ease-out;
    }

    @keyframes bump {
        0% { transform: scale(1); }
        30% { transform: scale(1.4); }
        60% { transform: scale(0.9); }
        100% { transform: scale(1); }
    }

    /* Опционально: если иконка - это SVG внутри IconButton, 
       можно менять ей цвет плавно через transition */
    :deep(.icon) {
        transition: fill 0.2s ease, transform 0.2s ease;
    }
</style>
